import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Login from "./Login";
import Home from "./Home";
import Operations from "./Operations";
import Sales from "./Sales";
import Settings from "./Settings";
import Ads from "./Ads"; // Import the new Ads component
import "./AppStyles.css";
import SalesStat from "./SalesStat";
import "./AppStyles.css";
import SalesTarget from "./SalesTarget";
import BackupRestore from "./BackupRestore";
import LogsPage from "./pages/LogsPage";
import { isUserMarkedFor2FA, clearUserFrom2FARequired, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { generateTwoFactorSecret } from "./enforceTwoFactor";
import TwoFactorAuth from "./TwoFactorAuth";
import { DataProvider } from "./contexts/DataContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import EditTransactionPage from "./pages/EditTransactionPage";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [needs2FASetup, setNeeds2FASetup] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [qrCodeData, setQrCodeData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [checking2FA, setChecking2FA] = useState(false);
  const [handledViaEvent, setHandledViaEvent] = useState(false); // Flag to track if we handled 2FA via custom event
  const [authProcessing, setAuthProcessing] = useState(false); // Pre-emptive UI state to prevent navigation flashes
  const authProcessingRef = useRef(false); // Ref to track auth processing without triggering effect re-runs

  useEffect(() => {
    const auth = getAuth();
    
    const handleAuthStateChange = async (authUser) => {
      console.log("🔍 Auth state changed:", authUser ? `User found: ${authUser.email}` : "No user");

      if (authUser) {
        // CRITICAL: If auth processing is active, don't proceed with normal auth flow
        // This prevents setting the user state while we're still determining 2FA requirements
        if (authProcessingRef.current) {
          console.log("🔍 Auth processing is active, deferring auth state handling");
          return;
        }
        
        setChecking2FA(true); // Start checking 2FA status
        
        // Check if user is marked for 2FA verification (meaning they need to verify)
        const marked = isUserMarkedFor2FA(authUser.uid);
        console.log("🔍 User marked for 2FA (localStorage):", marked);
        
        // If user is marked for 2FA, they need to verify
        if (marked) {
          // If we've already handled this via the custom event, skip the database lookup
          if (handledViaEvent) {
            console.log("🔍 Already handled via custom event, skipping database lookup");
            setChecking2FA(false);
            return;
          }
          
          console.log("🔍 User needs 2FA verification - doing database lookup");
          
          // Check if this user has 2FA set up or needs initial setup
          try {
            const userRef = doc(db, "users", authUser.uid);
            const userDoc = await getDoc(userRef);
            
            const has2FAEnabled = userDoc.exists() && userDoc.data().twoFactorEnabled;
            console.log("🔍 User has 2FA enabled:", has2FAEnabled);
            
            // Store email for 2FA screen
            localStorage.setItem("2fa_pending_email", authUser.email);
            localStorage.setItem("2fa_pending_userid", authUser.uid);

            setUserEmail(authUser.email);
            setUserId(authUser.uid);
            
            if (has2FAEnabled) {
              // User has 2FA enabled, show verification screen
              setNeedsTwoFactor(true);
              setNeeds2FASetup(false);
            } else {
              // User needs to set up 2FA first
              console.log("🔍 User needs 2FA setup");
              setNeeds2FASetup(true);
              setNeedsTwoFactor(false);
              
              // Generate QR code for setup
              try {
                const twoFactorData = await generateTwoFactorSecret(
                  authUser.uid,
                  authUser.email
                );
                setQrCodeData(twoFactorData);
                console.log("🔍 Generated 2FA secret for setup");
              } catch (error) {
                console.error("Error generating 2FA secret:", error);
              }
            }

            // DON'T sign out - keep user authenticated for Firestore access during 2FA
            setUser(null); // Just clear the local user state
            setChecking2FA(false); // Done checking 2FA status
          } catch (error) {
            console.error("Error checking user 2FA status:", error);
            // Default to verification screen if we can't determine the status
            setNeedsTwoFactor(true);
            setNeeds2FASetup(false);
            setUser(null);
            setChecking2FA(false); // Done checking 2FA status
          }
        } else {
          // User is NOT marked for 2FA, meaning they're either:
          // 1. Don't have 2FA enabled, OR
          // 2. Already completed 2FA verification for this session
          console.log("🔍 User not marked for 2FA - proceeding to home");
          
          // Clear auth processing since authentication is complete
          setAuthProcessing(false);
          authProcessingRef.current = false;
          
          // User is authenticated and doesn't need 2FA verification
          setUser(authUser);
          setNeedsTwoFactor(false);
          setNeeds2FASetup(false);
          setChecking2FA(false); // Done checking 2FA status

          // Clear any pending 2FA info
          localStorage.removeItem("2fa_pending_email");
          localStorage.removeItem("2fa_pending_userid");
          setQrCodeData(null);
        }
      } else {
        // User is not authenticated
        console.log("🔍 User not authenticated");
        setUser(null);
        setNeedsTwoFactor(false);
        setNeeds2FASetup(false);
        setAuthProcessing(false); // Clear auth processing when not authenticated
        authProcessingRef.current = false;

        // Clear any pending 2FA info when user is not authenticated
        localStorage.removeItem("2fa_pending_email");
        localStorage.removeItem("2fa_pending_userid");
        setQrCodeData(null);
        setHandledViaEvent(false); // Reset the flag
      }
      setLoading(false);
    };

    const unsubscribe = onAuthStateChanged(auth, handleAuthStateChange);
    
    // Listen for custom 2FA marked event from Login.js
    const handle2FAMarked = (event) => {
      console.log("🔍 Received 2FA marked event:", event.detail);
      const { userId, email, has2FAEnabled } = event.detail;
      
      // Mark that we've handled 2FA via the custom event (skip database lookup in auth handler)
      setHandledViaEvent(true);
      
      // Clear the auth processing overlay since we're now showing 2FA UI
      setAuthProcessing(false);
      authProcessingRef.current = false;
      
      // Set up the 2FA UI immediately without database lookup
      setChecking2FA(false); // We're done checking
      setUserEmail(email);
      setUserId(userId);
      
      // Store pending user info for 2FA screen
      localStorage.setItem("2fa_pending_email", email);
      localStorage.setItem("2fa_pending_userid", userId);
      
      if (has2FAEnabled) {
        // User has 2FA enabled, show verification screen immediately
        console.log("🔍 Showing 2FA verification screen immediately");
        setNeedsTwoFactor(true);
        setNeeds2FASetup(false);
      } else {
        // User needs to set up 2FA first
        console.log("🔍 Showing 2FA setup screen immediately");
        setNeeds2FASetup(true);
        setNeedsTwoFactor(false);
        
        // Generate QR code for setup
        generateTwoFactorSecret(userId, email)
          .then(twoFactorData => {
            setQrCodeData(twoFactorData);
            console.log("🔍 Generated 2FA secret for setup");
          })
          .catch(error => {
            console.error("Error generating 2FA secret:", error);
          });
      }
    };
    
    // Listen for auth processing start from Login.js
    const handleAuthStart = () => {
      console.log("🔍 Auth processing started - showing overlay");
      setAuthProcessing(true);
      authProcessingRef.current = true;
    };
    
    // Listen for auth processing complete
    const handleAuthComplete = () => {
      console.log("🔍 Auth processing complete - hiding overlay");
      setAuthProcessing(false);
      authProcessingRef.current = false;
      
      // Re-trigger auth state check after processing is complete for non-2FA users
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log("🔍 Re-checking auth state after processing complete for non-2FA user");
        handleAuthStateChange(currentUser);
      }
    };
    
    window.addEventListener('2fa-marked', handle2FAMarked);
    window.addEventListener('auth-processing-start', handleAuthStart);
    window.addEventListener('auth-processing-complete', handleAuthComplete);
    
    return () => {
      unsubscribe();
      window.removeEventListener('2fa-marked', handle2FAMarked);
      window.removeEventListener('auth-processing-start', handleAuthStart);
      window.removeEventListener('auth-processing-complete', handleAuthComplete);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handle2FAVerified = () => {
    console.log("2FA verified successfully");

    // Clear the 2FA requirement for this user
    if (userId) {
      clearUserFrom2FARequired(userId);
    }

    // Clear the pending 2FA info
    localStorage.removeItem("2fa_pending_email");
    localStorage.removeItem("2fa_pending_userid");

    setNeedsTwoFactor(false);
    setNeeds2FASetup(false);
    setQrCodeData(null);
    setHandledViaEvent(false); // Reset the flag
    setAuthProcessing(false); // Clear auth processing after 2FA completion
    authProcessingRef.current = false;

    // Get the current authenticated user and set them as logged in
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      console.log("Setting user as authenticated after 2FA verification");
      setUser(currentUser);
      // The Routes will automatically navigate to /home because user is now set
    } else {
      console.log("No current user found after 2FA, redirecting to login");
      window.location.href = "/";
    }
  };

  if (loading) {
    console.log("Showing loading screen");
    return <div>Loading...</div>;
  }

  if (needsTwoFactor) {
    console.log("Showing 2FA verification screen for user:", userEmail);
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>Two-Factor Authentication Required</h2>
          <p>Please enter your verification code to continue.</p>
          <TwoFactorAuth onVerified={handle2FAVerified} userEmail={userEmail} />
        </div>
      </div>
    );
  }

  if (needs2FASetup) {
    console.log("Showing 2FA setup screen for user:", userEmail);
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>Set Up Two-Factor Authentication</h2>
          <p>
            For enhanced security, your account requires two-factor
            authentication.
          </p>
          <p>Please scan the QR code below with Google Authenticator app:</p>

          {qrCodeData && (
            <div className="qr-container">
              <img src={qrCodeData.qrCodeUrl} alt="QR Code" />
              <p>
                Or manually enter this code:{" "}
                <strong>{qrCodeData.secret}</strong>
              </p>
            </div>
          )}

          <TwoFactorAuth
            onVerified={handle2FAVerified}
            userEmail={userEmail}
            preGeneratedSecret={qrCodeData?.secret}
            skipSecretGeneration={true}
          />
        </div>
      </div>
    );
  }

  console.log("Showing main app routing, user:", user?.email || "No user");

  // Show global auth processing overlay
  if (authProcessing) {
    return (
      <div className="auth-processing-overlay">
        <div className="auth-processing-content">
          <div className="loading-spinner"></div>
          <h3>Authenticating...</h3>
          <p>Please wait while we verify your credentials and check security settings.</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <DataProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route
                path="/"
                element={user && !needsTwoFactor && !needs2FASetup && !authProcessing ? <Navigate to="/home" /> : <Login />}
              />
              <Route path="/home" element={user && !needsTwoFactor && !needs2FASetup && !authProcessing ? <Home /> : <Navigate to="/" />}>
                <Route path="operations" element={<Operations />} />
                <Route path="sales" element={<Sales />} />
                <Route path="sales/edit/:transactionId" element={<EditTransactionPage />} />
                <Route path="settings" element={<Settings />} />
                <Route path="ads" element={<Ads />} />
                <Route path="sales-stat" element={<SalesStat />} />
                <Route path="sales-target" element={<SalesTarget />} />
                <Route path="backup-restore" element={<BackupRestore />} />
                <Route path="logs" element={<LogsPage />} />
              </Route>
            </Routes>
          </div>
        </Router>
      </DataProvider>
    </ThemeProvider>
  );
}

export default App;
